import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth, AuthProvider içinde kullanılmalıdır');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Component mount'ta authentication kontrol et
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      
      // AuthService'den user bilgilerini al
      if (authService.isAuthenticated()) {
        const userData = authService.getUser();
        setUser(userData);
        setIsAuthenticated(true);
        
        // Token geçerli mi kontrol et (optional)
        try {
          if (authService.isTokenExpired()) {
            throw new Error('Token süresi doldu');
          }
        } catch (error) {
          console.warn('Token validation failed:', error);
          // Token expired ise logout yap
          logout();
          return;
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth status check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      setLoading(true);
      
      const result = await authService.login(username, password);
      
      if (result.success) {
        setUser(result.data);
        setIsAuthenticated(true);
        navigate('/dashboard', { replace: true });
        return { success: true };
      }
      
      return { success: false, error: 'Giriş başarısız' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    try {
      // AuthService ile logout yap
      authService.logout();
      
      // State'leri temizle
      setUser(null);
      setIsAuthenticated(false);
      
      // Login sayfasına yönlendir
      navigate('/login', { replace: true });
      
      // Sayfayı yenile (tüm state'leri temizlemek için)
      setTimeout(() => {
        window.location.reload();
      }, 100);
      
    } catch (error) {
      console.error('Logout error:', error);
      // Hata olsa bile logout yap
      setUser(null);
      setIsAuthenticated(false);
      navigate('/login', { replace: true });
      window.location.reload();
    }
  };

  const updateUser = (userData) => {
    try {
      const updatedUser = authService.updateUser(userData);
      if (updatedUser) {
        setUser(updatedUser);
        return updatedUser;
      }
      return null;
    } catch (error) {
      console.error('Update user error:', error);
      return null;
    }
  };

  // API request wrapper
  const apiRequest = async (endpoint, options = {}) => {
    try {
      return await authService.apiRequest(endpoint, options);
    } catch (error) {
      // Token geçersizse logout yap
      if (error.message.includes('Authentication expired') || error.message.includes('Kimlik doğrulama süresi doldu')) {
        logout();
      }
      throw error;
    }
  };

  const value = {
    // State
    user,
    loading,
    isAuthenticated,
    
    // Actions
    login,
    logout,
    updateUser,
    apiRequest,
    checkAuthStatus,
    
    // Utilities
    hasPermission: (permission) => authService.hasPermission(permission),
    getToken: () => authService.getToken(),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
